import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LayoutGrid, Menu, X, LogOut, ShoppingCart, LayoutDashboard, Plus, ArrowLeft, Contact } from 'lucide-react';

export default function AppHeader({ title, backTo, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {backTo ? (
            <Button variant="ghost" size="sm" onClick={() => navigate(backTo)} className="p-1 sm:p-2 text-slate-600 shrink-0" data-testid="back-button">
              <ArrowLeft size={20} />
            </Button>
          ) : (
            <LayoutGrid size={28} className="text-[#0052FF] shrink-0 sm:w-8 sm:h-8" />
          )}
          <h1 className="text-lg sm:text-2xl font-medium tracking-tight text-slate-900 truncate">{title || 'Hi Tech Industries'}</h1>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-2">
          {children}
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-slate-900" data-testid="logout-button">
            <LogOut size={18} className="mr-2" /> Logout
          </Button>
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden p-2 text-slate-600 hover:text-slate-900 rounded-sm"
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1 animate-in slide-in-from-top-2" data-testid="mobile-menu">
          {user?.role === 'admin' && (
            <>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/configurator'); setMenuOpen(false); }} data-testid="mobile-configurator-link">
                <LayoutGrid size={18} className="mr-3" /> Configurator
              </Button>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/dashboard'); setMenuOpen(false); }} data-testid="mobile-orders-link">
                <ShoppingCart size={18} className="mr-3" /> My Orders
              </Button>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/admin'); setMenuOpen(false); }} data-testid="mobile-admin-link">
                <LayoutDashboard size={18} className="mr-3" /> Admin Dashboard
              </Button>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/contacts'); setMenuOpen(false); }} data-testid="mobile-contacts-link">
                <Contact size={18} className="mr-3" /> Contacts
              </Button>
            </>
          )}
          {user?.role === 'customer' && (
            <>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/configurator'); setMenuOpen(false); }} data-testid="mobile-configurator-link">
                <Plus size={18} className="mr-3" /> New Quote
              </Button>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { navigate('/dashboard'); setMenuOpen(false); }} data-testid="mobile-orders-link">
                <ShoppingCart size={18} className="mr-3" /> My Orders
              </Button>
            </>
          )}
          <div className="border-t border-slate-100 pt-1 mt-1">
            <Button variant="ghost" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => { handleLogout(); setMenuOpen(false); }} data-testid="mobile-logout">
              <LogOut size={18} className="mr-3" /> Logout
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
